import type { Product } from './shopwareApi';
import { fetchPdpDataRaw } from './shopwareApi';
import { getCookie } from '../utils/cookieStorage';
import { formatPrice, getEffectiveCalculatedPrice, getFormattedListPrice } from './shopwareUtils';

// Non-httpOnly cookie set at mein-orthomol login and cleared at logout (SsoTokenCookieService).
const SSO_LOGIN_MARKER_COOKIE = 'orthomol_sso_timestamp';

/**
 * ORT-3109: is the visitor logged in to mein-orthomol? Reads the non-httpOnly SSO timestamp
 * cookie (presence = logged in), not any token. Gates whether we request customer-specific,
 * token-bearing (and therefore non-cacheable) /pdp-data. Works on the fully-cached PDP because
 * the cookie is per-browser, independent of the URL-keyed HTML cache.
 */
export function isCustomerLoggedIn(): boolean {
  return !!getCookie(SSO_LOGIN_MARKER_COOKIE);
}

/* ------------------------------------------------------------------ *
 * Raw envelope as returned by POST {storeApiBase}/pdp-data (valantic).
 * apiAlias: "array_struct"; one item per requested productNumber.
 * Shape verified live against stage-sw6 (2026-06) and the latest spec
 * (productNumbers[] request, countryIso, shippingMethods[].prices).
 * ------------------------------------------------------------------ */

export interface PdpRawReferencePrice {
  price: number;
  unitName?: string | null;
}

export interface PdpRawCalculatedPrice {
  unitPrice: number;
  // listPrice is the rule-based Streichpreis ("was" price), null when there is none.
  listPrice: { price: number; discount?: number; percentage?: number } | null;
  referencePrice: PdpRawReferencePrice | null;
}

export interface PdpRawDiscount {
  type: 'percentage' | 'absolute' | string;
  value: number;
  scope: string;
}

export interface PdpRawPromotionPrice {
  unitPrice: number;
  discount: number;
  discountPercentage: number;
}

export interface PdpRawPromotion {
  id: string;
  code: string;
  name: string;
  userDescription: string;
  visibleOnPdp: boolean;
  priceOnPdp: boolean;
  priority: number;
  useCodes: boolean;
  validFrom: string | null;
  validUntil: string | null;
  minOrderValue: number | null;
  discounts: PdpRawDiscount[];
  // Discounted price for this promotion, mirroring calculatedPrice / calculatedPrices:
  // calculatedPrice is the base, calculatedPrices[] the rule-based tiers.
  promotionPrices: {
    calculatedPrice: PdpRawPromotionPrice | null;
    calculatedPrices: PdpRawPromotionPrice[];
  } | null;
}

export interface PdpRawShippingPrice {
  gross: number;
  net: number;
  currencyId: string;
  // Order-value band this price applies to (gross). calculationEnd null = open-ended.
  calculationStart: number;
  calculationEnd: number | null;
  calculationType: string;
}

export interface PdpRawShippingMethod {
  name: string;
  deliveryTime: string | null;
  prices?: PdpRawShippingPrice[];
}

export interface PdpRawItem {
  productNumber: string;
  id: string;
  active: boolean;
  available: boolean;
  availableStock: number;
  deliveryTime: string | null;
  shippingMethods?: PdpRawShippingMethod[];
  // All product customFields are PIM-owned (synced into Shopware). We source them from
  // the embedded PIM JSON, so this is kept only as an escape hatch, not relied upon.
  customFields?: Record<string, unknown> | null;
  calculatedPrice: PdpRawCalculatedPrice | null;
  calculatedPrices: PdpRawCalculatedPrice[];
  promotions: PdpRawPromotion[];
}

export interface PdpDataResponse {
  apiAlias: string;
  items: PdpRawItem[];
}

/* ------------------------------------------------------------------ *
 * Normalized output consumed by the PDP.
 * ------------------------------------------------------------------ */

export interface PdpPromotion extends PdpRawPromotion {
  // calculatedPrices[0] when present, else calculatedPrice. Mirrors the base-price
  // rule used by getEffectiveCalculatedPrice() in shopwareUtils, so the promo
  // Streichpreis is computed against the same price the PDP actually shows.
  effectivePromotionPrice: PdpRawPromotionPrice | null;
}

export interface PdpVariantData {
  productNumber: string;
  // Product-compatible projection so the existing shopwareUtils price/availability
  // helpers (getEffectiveCalculatedPrice, checkProductAvailability, ...) work unchanged.
  product: Product;
  promotions: PdpPromotion[];
  shippingMethods: PdpRawShippingMethod[];
  // Lowest order value at which shipping becomes free (ORT-3090), or null if none.
  freeShippingThreshold: number | null;
  raw: PdpRawItem;
}

const effectivePromotionPrice = (promo: PdpRawPromotion): PdpRawPromotionPrice | null => {
  const tiers = promo.promotionPrices?.calculatedPrices;
  if (Array.isArray(tiers) && tiers.length > 0) return tiers[0];
  return promo.promotionPrices?.calculatedPrice ?? null;
};

const adaptPromotion = (promo: PdpRawPromotion): PdpPromotion => ({
  ...promo,
  effectivePromotionPrice: effectivePromotionPrice(promo),
});

/**
 * Project a raw /pdp-data item onto the Product shape the existing helpers expect.
 * Differences bridged here: deliveryTime is a string upstream (-> { name }), and the
 * reference-price unit label comes from referencePrice.unitName (-> product.unit).
 */
const toProduct = (item: PdpRawItem): Product => {
  const tiers = item.calculatedPrices;
  const effective = Array.isArray(tiers) && tiers.length > 0 ? tiers[0] : item.calculatedPrice;
  const unitName = effective?.referencePrice?.unitName ?? undefined;

  return {
    id: item.id,
    productNumber: item.productNumber,
    active: item.active,
    available: item.available,
    availableStock: item.availableStock,
    calculatedPrice: item.calculatedPrice ?? undefined,
    calculatedPrices: item.calculatedPrices ?? [],
    deliveryTime: item.deliveryTime ? { name: item.deliveryTime } : undefined,
    unit: unitName ? { translated: { shortCode: unitName }, shortCode: unitName } : undefined,
    customFields: item.customFields ?? undefined,
  } as unknown as Product;
};

export const adaptPdpItem = (item: PdpRawItem): PdpVariantData => ({
  productNumber: item.productNumber,
  product: toProduct(item),
  promotions: (item.promotions ?? []).map(adaptPromotion),
  shippingMethods: item.shippingMethods ?? [],
  freeShippingThreshold: deriveFreeShippingThreshold(item.shippingMethods),
  raw: item,
});

/**
 * Code-free promotion that yields the lowest price for this variant, or null.
 *
 * Same filter as ProductInfo.tsx (applicablePromotions/bestPromotion) minus the active-code branch,
 * and the twin of ShopwarePriceResolver::bestAutoPromotion() in PHP: only promotions flagged
 * priceOnPdp that carry a promotion price and need no code. A 0,00 EUR promotion price is valid.
 * Code-gated promotions stay out here: the islands have no "Anwenden" button (ORT-3060 decision).
 */
export function bestAutoPromotion(promotions: PdpPromotion[] | undefined): PdpPromotion | null {
  return (promotions ?? [])
    .filter((p) => p.priceOnPdp && p.effectivePromotionPrice != null && !p.useCodes)
    .reduce<PdpPromotion | null>(
      (best, p) => (best == null || p.effectivePromotionPrice!.unitPrice < best.effectivePromotionPrice!.unitPrice ? p : best),
      null
    );
}

/** Single-purchase price of one variant as the shopper sees it, see resolveDisplayPrice(). */
export interface DisplayPrice {
  /** What the shopper pays, e.g. "12,99 €"; null when the variant has no price */
  unitPrice: string | null;
  /** Struck original: the normal price when a promotion wins, else the rule-based Shopware list price */
  listPrice: string | null;
  /** Numeric main price for comparisons (cheapest variant, "ab" range); null when unitPrice is null */
  amount: number | null;
  /** The code-free promotion that set the price, or null */
  promotion: PdpPromotion | null;
}

/**
 * Display price of one /pdp-data variant with the PDP's single-purchase rule (ProductInfo.tsx,
 * singleMain/singleStrike) and the snapshot's rule (ShopwarePriceResolver::resolve()):
 * a code-free promotion price becomes the main price and the normal price the struck original,
 * beating the rule-based list price; otherwise the normal price with the list price struck.
 * Used by the hero island and the add-to-cart modal so they agree with the PDP and the cards.
 */
export function resolveDisplayPrice(data: PdpVariantData | null | undefined): DisplayPrice {
  const product = data?.product ?? null;
  const base = getEffectiveCalculatedPrice(product)?.unitPrice || null; // falsy = no price, like getFormattedUnitPrice()
  const promotion = bestAutoPromotion(data?.promotions);
  if (promotion?.effectivePromotionPrice) {
    const amount = promotion.effectivePromotionPrice.unitPrice;
    return {
      unitPrice: `${formatPrice(amount)} €`,
      listPrice: base ? `${formatPrice(base)} €` : null,
      amount,
      promotion,
    };
  }
  return {
    unitPrice: base ? `${formatPrice(base)} €` : null,
    listPrice: getFormattedListPrice(product),
    amount: base,
    promotion: null,
  };
}

/**
 * Lowest order value (gross) at which shipping becomes free, derived from the shipping
 * methods' price tiers (ORT-3090, replaces the hardcoded 50 EUR). Takes the smallest
 * calculationStart whose gross is 0 across all methods, or null if no free tier exists.
 */
export function deriveFreeShippingThreshold(methods: PdpRawShippingMethod[] | undefined): number | null {
  if (!methods) return null;
  let threshold: number | null = null;
  for (const method of methods) {
    for (const price of method.prices ?? []) {
      if (price.gross === 0 && (threshold === null || price.calculationStart < threshold)) {
        threshold = price.calculationStart;
      }
    }
  }
  return threshold;
}

/**
 * Choose which promotion to surface as a clickable Anwenden offer (ORT-3060): only those that
 * are visibleOnPdp AND actually need a code (useCodes + a code). Auto-firing promotions
 * (useCodes:false) are not offers — they apply with no action and are reflected directly in the
 * PDP price instead. Among the candidates the lowest `priority` value wins (assumed 1 = highest;
 * confirm with valantic). Returns null when none qualify.
 */
export function pickPdpPromotion(promotions: PdpPromotion[]): PdpPromotion | null {
  const offers = promotions.filter((p) => p.visibleOnPdp && p.useCodes && !!p.code);
  if (offers.length === 0) return null;
  return offers.slice().sort((a, b) => a.priority - b.priority)[0];
}

export type CountryIso = 'DE' | 'AT';
const ALLOWED_COUNTRIES: readonly string[] = ['DE', 'AT'];

/**
 * Test toggle for ORT-3064: read `?country=at` from the URL to preview AT
 * pricing/promotions without any AT-specific UI. Returns an uppercased ISO when valid,
 * else null (server defaults to DE). Drives `countryIso` on /pdp-data and `shippingTo`
 * on cart/checkout.
 */
export function getCountryParamFromUrl(): CountryIso | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('country');
  if (!raw) return null;
  const iso = raw.toUpperCase();
  return ALLOWED_COUNTRIES.includes(iso) ? (iso as CountryIso) : null;
}

/**
 * High-level: fetch + adapt /pdp-data for the given product numbers. Returns a map keyed
 * by productNumber so a variant and its abo (-1M) sibling can be looked up from one call.
 */
export async function fetchPdpData(
  productNumbers: string[],
  countryIso?: string | null,
  opts?: { withCustomerToken?: boolean }
): Promise<Record<string, PdpVariantData>> {
  if (productNumbers.length === 0) return {};
  const res = await fetchPdpDataRaw(productNumbers, countryIso ?? undefined, opts?.withCustomerToken ?? false);
  const map: Record<string, PdpVariantData> = {};
  for (const item of res.items ?? []) {
    if (item.productNumber) map[item.productNumber] = adaptPdpItem(item);
  }
  return map;
}
